// Thư mục: src/stores/adminChecklistStore.js
import { defineStore } from "pinia";
import { AdminChecklistApi } from "@/services/adminChecklistService";

export const useAdminChecklistStore = defineStore("adminChecklist", {
  state: () => ({
    // State cho danh sách
    isLoadingList: false,
    listError: null,
    items: [], // Danh sách log (XeChecklistLogResponse)
    page: 0,
    size: 10,
    sort: "thoiDiemThucHien,desc",
    totalPages: 1,
    totalElements: 0,

    // State cho Filters (được component set)
    filterXeId: null,
    filterTaiXeId: null,
    filterHasError: null, // null = Cả hai, true = Lỗi, false = OK

    // State cho Modal Chi tiết
    isLoadingDetail: false,
    detailError: null,
    currentLogDetail: null, // XeChecklistLogDetailResponse { log, items: [...] }

    // State cho Options (dùng cho filter)
    isLoadingOptions: false,
    xeOptions: [], // [{id, bienSo}]
    taiXeOptions: [], // [{idNguoiDung, hoTen}]
  }),

  getters: {
    currentPage(state) {
      return state.page + 1;
    },
  },

  actions: {
    // --- Actions Phân trang & Filter ---
    setPage(p1) {
      this.page = Math.max(0, p1 - 1);
    },
    setSize(s) {
      this.size = s;
      this.page = 0; // Reset về trang đầu
    },
    /**
     * Action này được gọi bởi component khi bấm nút Lọc
     */
    async setFiltersAndFetch(filters) {
      // { xeId, taiXeId, hasError }
      this.filterXeId = filters.xeId || null;
      this.filterTaiXeId = filters.taiXeId || null;
      this.filterHasError = filters.hasError; // Cho phép null
      this.page = 0; // Reset về trang đầu
      await this.fetchList(); // Tải lại với filter mới
    },

    // --- Action Tải Danh sách Log ---
    async fetchList() {
      this.isLoadingList = true;
      this.listError = null;
      try {
        const params = {
          page: this.page,
          size: this.size,
          sort: this.sort,
          // Gửi các filter state đi
          xeId: this.filterXeId,
          taiXeId: this.filterTaiXeId,
          hasError: this.filterHasError,
        };
        const data = await AdminChecklistApi.getHistory(params);
        this.items = data.content || [];
        this.totalPages = data.totalPages ?? 1;
        this.totalElements = data.totalElements ?? this.items.length;
        this.page = data.number ?? 0;
      } catch (e) {
        this.listError =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi tải lịch sử checklist";
        console.error("fetchList AdminChecklist error:", e);
      } finally {
        this.isLoadingList = false;
      }
    },

    // --- Action Tải Chi tiết Log (cho Modal) ---
    async fetchLogDetail(logId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      this.currentLogDetail = null;
      try {
        this.currentLogDetail = await AdminChecklistApi.getLogDetail(logId);
      } catch (e) {
        this.detailError =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi tải chi tiết checklist";
        console.error("fetchLogDetail error:", e);
      } finally {
        this.isLoadingDetail = false;
      }
    },
    clearLogDetail() {
      this.currentLogDetail = null;
      this.detailError = null;
    },

    // --- Action Tải Options cho Filter ---
    async fetchOptionsIfNeeded() {
      if (this.xeOptions.length > 0 || this.isLoadingOptions) return; // Chỉ tải 1 lần
      this.isLoadingOptions = true;
      try {
        const [xeData, taiXeData] = await Promise.all([
          AdminChecklistApi.getXeOptions(),
          AdminChecklistApi.getTaiXeOptions(),
        ]);
        // Giả sử API Xe trả về {id, bienSo}
        this.xeOptions = xeData || [];
        // Giả sử API Tài xế trả về {idNguoiDung, hoTen}
        this.taiXeOptions = (taiXeData || []).map((nd) => ({
          id: nd.idNguoiDung, // Chuẩn hóa ID
          hoTen: nd.hoTen,
        }));
      } catch (e) {
        console.error("Lỗi tải options Xe/Tài xế:", e);
      } finally {
        this.isLoadingOptions = false;
      }
    },
  },
});
